import React, {
  PropsWithChildren,
  useEffect,
  useMemo,
} from 'react';
import isEmpty from 'lodash/isEmpty';
import clsx from 'clsx';
import {
  ImageSize,
  Props as ImageProps,
} from 'pages/Dashboard/components/Image';
import {
  useMedia,
} from 'core/hooks';
import {
  formatDate,
} from 'utils/date';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import {
  getPatientAge,
} from 'utils/misc';
import {
  Patient,
} from 'pages/Dashboard/types/patient';
import Name from 'pages/Dashboard/components/Name';
import Tag from 'core/components/Tag';
import compact from 'lodash/compact';
import {
  getPatientChartPath,
} from 'pages/Dashboard/pages/Charts/utils';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';

type Part = 'nameRoot' | 'nameContainer';

type Props = PropsWithChildren & {
  patient: Patient;
  image?: Omit<ImageProps, 'src' | 'alt'>;
  classes?: Partial<Record<Part, string>>;
  className?: string;
  isLink?: boolean;
  to?: string;
};

const fontSizes: Record<ImageSize, string[]> = {
  xl: ['text-lg', 'text-base'],
  lg: ['text-base', 'text-sm'],
  md: ['text-sm', 'text-xs'],
};

type PatientInfoTagsProps = Pick<Patient, 'isDeceased' | 'isInactive'>;

export function PatientInfoTags({ isDeceased, isInactive }: PatientInfoTagsProps) {
  const labels = useMemo(() => compact([
    isDeceased ? 'Deceased' : undefined,
    isInactive ? 'Inactive' : undefined,
  ]).join(' • '), [isDeceased, isInactive]);

  return isEmptyString(labels) ? null : (
    <Tag
      tagName={labels}
      className="!text-xs !font-medium rounded-full !bg-red-100 !text-red-700 !whitespace-nowrap"
    />
  );
}

export default function PatientInfo({
  patient,
  image = {},
  isLink = true,
  className,
  classes = {},
  to,
  children,
}: Props) {
  const { getMediaPathById, requestMedia } = useMedia();
  const { isDeceased, isInactive } = patient;
  const { patientId, dosespotError } = patient;
  const chartPath = to ?? getPatientChartPath(patientId);

  useEffect(() => {
    requestMedia([patientId ?? 0]);
  }, [patientId]);

  const errorTitle = isDeceased ? 'Patient Deceased' : dosespotError?.title;
  const [nameFontSize, otherFontSize] = fontSizes[image?.size ?? 'lg'];
  const patientDisplayName = getDisplayName(patient?.formattedNames);

  return (
    <div className={clsx('flex gap-2 text-base', className)}>
      <Name
        {...patient?.formattedNames}
        name={isEmpty(patient)
          ? '--'
          : (
            <div className="flex gap-2 items-center populate-vertical-list-text">
              {(isDeceased || isInactive) && (
                <span className={clsx('populate-list-item flex items-center', otherFontSize)}>
                  <PatientInfoTags isDeceased={isDeceased} isInactive={isInactive} />
                </span>
              )}
              <span className={clsx('populate-list-item', nameFontSize)}>
                {patientDisplayName}
              </span>
              <span className={clsx('populate-list-item', otherFontSize)}>
                {formatDate(patient?.dateOfBirth ?? '')}
              </span>
              {!isDeceased && (
                <span className={clsx('populate-list-item', otherFontSize)}>
                  {getPatientAge(patient?.dateOfBirth ?? '')}
                </span>
              )}
              <span className={clsx('populate-list-item', otherFontSize)}>
                {patient?.genderDescription ?? ''}
              </span>
            </div>
          )}
        link={isLink && notEmptyString(chartPath) ? { to: chartPath } : undefined}
        classes={{
          root: classes?.nameContainer,
          nameRoot: classes?.nameRoot,
        }}
        image={{
          ...image,
          src: getMediaPathById(patientId ?? 0) ?? undefined,
          alt: patientDisplayName,
        }}
        error={errorTitle ?? undefined}
      >
        {children}
      </Name>
    </div>
  );
}
